import { useState, useEffect, useMemo } from 'react';
import { COMPANY_TYPE_TEMPLATES, PERSONALIZED_TEMPLATE_STORAGE_KEY } from '../constants/constants';
import type { GeneratedTemplateType } from '../types/util-types';

interface TemplateOption {
  value: string;
  label: string;
}

interface ITemplateSelectionReturn {
  companyType: string;
  selectedTemplate: string;
  companyTypeOptions: TemplateOption[];
  templateOptions: TemplateOption[];
  setCompanyType: (value: string) => void;
  setSelectedTemplate: (value: string) => void;
  storeTemplateSelection: () => void;
}

export const useTemplateSelection = (templateType: GeneratedTemplateType): ITemplateSelectionReturn => {
  const [companyType, setCompanyType] = useState<string>(
    COMPANY_TYPE_TEMPLATES[COMPANY_TYPE_TEMPLATES.length - 1].value
  );
  
  const selectedCompanyTypeObj = useMemo(() => 
    COMPANY_TYPE_TEMPLATES.find(type => type.value === companyType),
    [companyType]
  );
  
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    selectedCompanyTypeObj?.templateTypes?.[templateType]?.[0]?.value || ''
  );

  useEffect(() => {
    const firstTemplateValue = selectedCompanyTypeObj?.templateTypes?.[templateType]?.[0]?.value;
    if (firstTemplateValue) {
      setSelectedTemplate(firstTemplateValue);
    }
  }, [companyType, templateType, selectedCompanyTypeObj]);

  useEffect(() => {
    storeTemplateSelection();
  }, []);

  const companyTypeOptions = useMemo(() => 
    COMPANY_TYPE_TEMPLATES.map(type => ({
      value: type.value,
      label: type.label
    })),
    []
  );

  const templateOptions = useMemo(() => 
    selectedCompanyTypeObj?.templateTypes?.[templateType]?.map(template => ({
      value: template.value,
      label: template.label
    })) || [],
    [selectedCompanyTypeObj, templateType]
  );

  const storeTemplateSelection = (): void => {
    sessionStorage.setItem(PERSONALIZED_TEMPLATE_STORAGE_KEY, JSON.stringify({
      companyType,
      templateType: selectedTemplate,
    }));
  };

  return {
    companyType,
    selectedTemplate,
    companyTypeOptions,
    templateOptions,
    setCompanyType,
    setSelectedTemplate,
    storeTemplateSelection
  };
}; 